import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText,
  Divider,
  Backdrop,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import { MessageSquare, Settings, LogOut, Menu, X } from 'lucide-react';
import { logout } from '../../utils/auth.js';
import compagnioLogo from '../../assets/compagnio_logo.svg';
import { useTranslation } from 'react-i18next';

const MobileLayout = ({ children, title }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const getPageTitle = () => {
    if (title) return title;
    
    const path = location.pathname;
    if (path === '/') return t('mobileTitle.conversations');
    if (path === '/recording') return t('mobileTitle.recording');
    if (path === '/summary') return t('mobileTitle.summary');
    if (path === '/settings') return t('mobileTitle.settings');
    return 'Maris Med';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          backgroundColor: '#FFFFFF',
          color: '#000000',
          borderBottom: '1px solid #E4E4E7'
        }}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img 
              src={compagnioLogo} 
              alt="Compagnio Logo" 
              style={{ 
                height: 40,
                marginRight: 12
              }} 
            />
            <Typography 
              variant="h2" 
              component="div" 
            >
              {getPageTitle()}
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={() => setDrawerOpen(true)}
          >
            <Menu />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Blurred backdrop */}
      <Backdrop
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          zIndex: (theme) => theme.zIndex.drawer - 1,
        }}
        open={drawerOpen}
        onClick={() => setDrawerOpen(false)}
      />

      {/* Drawer for navigation */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: 250,
            boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
            borderTopLeftRadius: '6px',
            borderBottomLeftRadius: '6px',
            border: 'none',
            paddingTop: 1.4,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
          disableScrollLock: true, // Prevents content shift
        }}
      >
        <Box sx={{ width: 250, display: 'flex', flexDirection: 'column', height: '100%' }} role="presentation">
          {/* Close button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 2 }}>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setDrawerOpen(false)}
              size="small"
            >
              <X />
            </IconButton>
          </Box>
          
          {/* Main navigation items */}
          <List sx={{ pl: 1 }}>
            <ListItem 
              component="div" 
              onClick={() => handleNavigation('/')} 
              selected={location.pathname === '/'} 
              variant="menuItem"
            >
              <ListItemIcon 
                variant="menuItem"
              >
                <MessageSquare strokeWidth={1.5} />
              </ListItemIcon>
              <ListItemText 
                primary={t('mobileNavigation.conversations')} 
                variant="menuItem"
              />
            </ListItem>
            <ListItem 
              component="div" 
              onClick={() => handleNavigation('/settings')} 
              selected={location.pathname === '/settings'} 
              variant="menuItem"
            >
              <ListItemIcon 
                variant="menuItem"
              >
                <Settings strokeWidth={1.5} />
              </ListItemIcon>
              <ListItemText 
                primary={t('mobileNavigation.settings')} 
                variant="menuItem"
              />
            </ListItem>
          </List>
          
          {/* Language selector and logout */}
          <Box sx={{ mt: 'auto', mb: 2, pl: 1 }}>
            <List>
              <ListItem 
                component="div" 
                variant="menuItem"
                sx={{ py: 1, pl: 1 }}
              >
                <FormControl 
                  sx={{ 
                    minWidth: 100,
                    '& .MuiSelect-select': {
                      paddingX: 0,
                      fontSize: 14,
                    },
                  }}
                >
                  <Select
                    value={i18n.language}
                    onChange={(e) => changeLanguage(e.target.value)}
                    variant="standard"
                    disableUnderline
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    <MenuItem value='en'>{t('languages.en')}</MenuItem>
                    <MenuItem value='de'>{t('languages.de')}</MenuItem>
                  </Select>
                </FormControl>
              </ListItem>
              
              <Divider sx={{ my: 1 }} />
              
              <ListItem 
                component="div" 
                onClick={() => logout()} 
                variant="menuItem"
              >
                <ListItemIcon variant="menuItem">
                  <LogOut strokeWidth={1.5} />
                </ListItemIcon>
                <ListItemText 
                  primary="Logout" 
                  variant="menuItem"
                />
              </ListItem>
            </List>
          </Box>
        </Box>
      </Drawer>

      {/* Main content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {children}
      </Box>
    </Box>
  );
};

export default MobileLayout; 